import { claimBlocker } from './claim';
import type { DocketProject, DocketSession } from './types';

export interface Ranked { readonly session: DocketSession; readonly reason: string; readonly available: boolean }

const tierOf = (s: DocketSession): number => (Number.isInteger(s.tier) && s.tier >= 0 && s.tier <= 3 ? s.tier : 3);
const areaOf = (s: DocketSession): number => (Number.isFinite(s.areaSize) ? (s.areaSize as number) : 0);

const flags = (s: DocketSession): string[] => [
  s.userBlocking ? 'user-blocking' : '',
  `tier ${tierOf(s)}`,
  s.nickPicked ? 'Nick-picked' : '',
  s.moneyOrCustomerData ? 'money/customer data' : '',
].filter(Boolean);

const cmp = (a: DocketSession, b: DocketSession): number =>
  Number(!!b.userBlocking) - Number(!!a.userBlocking)
  || tierOf(a) - tierOf(b)
  || Number(!!b.nickPicked) - Number(!!a.nickPicked)
  || Number(!!b.moneyOrCustomerData) - Number(!!a.moneyOrCustomerData)
  || areaOf(b) - areaOf(a)
  || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/**
 * Order: claimable now (by the SAME rule claimSession uses) > rows that could be worked but are
 * blocked (a dependent always below its blocker, cycles listed last and named) > everything else.
 * Ties end on id, so input order never matters.
 */
export function rankSessions(sessions: readonly DocketSession[], projects: readonly DocketProject[], nowIso: string): Ranked[] {
  const title = new Map(projects.map((p) => [p.id, p.title]));
  const workable = sessions.filter((s) => s.status === 'READY' || s.status === 'WAITS-TIME');
  const blockers = new Map(workable.map((s) => [s.id, claimBlocker(s, sessions, nowIso)] as const));
  const free = workable.filter((s) => blockers.get(s.id) === null).sort(cmp);
  const waiting = workable.filter((s) => blockers.get(s.id) !== null).sort(cmp);
  const rest = sessions.filter((s) => !workable.includes(s)).sort(cmp);
  const why = (s: DocketSession, extra = ''): string =>
    `${flags(s).join(', ')}${title.has(s.projectId) ? ` · project: ${title.get(s.projectId)}` : ''}${extra}`;

  // Topological pass over the waiting rows: a row goes after any waiting row it depends on.
  const ids = new Set(waiting.map((s) => s.id));
  const placed = new Set<string>();
  const ordered: Ranked[] = [];
  let left = [...waiting];
  while (left.length > 0) {
    const next = left.find((s) => s.dependsOn.every((d) => !ids.has(d) || placed.has(d)));
    if (!next) {
      ordered.push(...left.map((s) => ({ session: s, available: false, reason: why(s, ' · dependency cycle') })));
      break;
    }
    placed.add(next.id);
    ordered.push({ session: next, available: false, reason: why(next, ` · ${blockers.get(next.id)?.reason ?? 'blocked'}`) });
    left = left.filter((s) => s !== next);
  }
  return [
    ...free.map((s) => ({ session: s, available: true, reason: why(s) })),
    ...ordered,
    ...rest.map((s) => ({ session: s, available: false, reason: why(s, ` · ${s.status}`) })),
  ];
}
